import React, {
  forwardRef,
  useState,
  useCallback,
  UIEvent,
  UIEventHandler,
} from "react";

import classnames from "classnames";

import {
  StyledScrollShadow,
  StyledScrollShadowBottom,
  StyledScrollShadowLeft,
  StyledScrollShadowRight,
  StyledScrollShadowTop,
} from "./Styles";

export interface IScrollShadowProps {
  /** Children */
  children: (props: { onScroll: UIEventHandler }) => JSX.Element;
  /** Applies passed classes */
  className?: string;
  /** Disable vertical shadow */
  disableVertical?: boolean;
  /** Disable horizontal shadow */
  disableHorizontal?: boolean;
  /** Invert horizontal shadow logic */
  invertHorizontal?: boolean;
  /** Invert vertical shadow logic */
  invertVertical?: boolean;
  /** Inner gradient color */
  innerColor?: string;
  /** Outer gradient color */
  outerColor?: string;
}

export const ScrollShadow = forwardRef<HTMLDivElement, IScrollShadowProps>(
  (
    {
      children,
      className,
      disableVertical = false,
      disableHorizontal = false,
      invertHorizontal = false,
      invertVertical = false,
      innerColor,
      outerColor,
      ...rest
    },
    ref
  ) => {
    const [opacity, setOpacity] = useState({
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    });

    const handleScroll = useCallback(
      (event: UIEvent) => {
        const { scrollHeight, clientHeight, scrollWidth, clientWidth } =
          event.target as Element;
        let { scrollTop, scrollLeft } = event.target as Element;

        if (scrollLeft < 0) {
          scrollLeft *= -1;
        }
        if (scrollTop < 0) {
          scrollTop *= -1;
        }

        setOpacity((prevState) => {
          const top = (1 / 20) * Math.min(scrollTop, 20);
          const bottomScrollTop = scrollHeight - clientHeight;
          const bottom =
            (1 / 20) *
            (bottomScrollTop - Math.max(scrollTop, bottomScrollTop - 20));
          const left = (1 / 20) * Math.min(scrollLeft, 20);
          const offset = scrollWidth - clientWidth;
          const right = (1 / 20) * (offset - Math.max(scrollLeft, offset - 20));

          const newState = { top: 0, bottom: 0, left: 0, right: 0 };

          if (!disableVertical) {
            newState.top = invertVertical ? bottom : top;
            newState.bottom = invertVertical ? top : bottom;
          }

          if (!disableHorizontal) {
            newState.left = invertHorizontal ? right : left;
            newState.right = invertHorizontal ? left : right;
          }

          if (
            newState.top !== prevState.top ||
            newState.bottom !== prevState.bottom ||
            newState.left !== prevState.left ||
            newState.right !== prevState.right
          ) {
            return newState;
          }

          return prevState;
        });
      },
      [invertHorizontal, invertVertical, disableVertical, disableHorizontal]
    );

    return (
      <StyledScrollShadow
        {...rest}
        ref={ref}
        className={classnames("c-scroll-shadow", className)}
      >
        {children({ onScroll: handleScroll })}
        {!disableVertical ? (
          <>
            <StyledScrollShadowTop
              $innerColor={innerColor}
              $outerColor={outerColor}
              style={{ opacity: opacity.top }}
            />
            <StyledScrollShadowBottom
              $innerColor={innerColor}
              $outerColor={outerColor}
              style={{ opacity: opacity.bottom }}
            />
          </>
        ) : null}
        {!disableHorizontal ? (
          <>
            <StyledScrollShadowLeft
              $innerColor={innerColor}
              $outerColor={outerColor}
              style={{ opacity: opacity.left }}
            />
            <StyledScrollShadowRight
              $innerColor={innerColor}
              $outerColor={outerColor}
              style={{ opacity: opacity.right }}
            />
          </>
        ) : null}
      </StyledScrollShadow>
    );
  }
);

ScrollShadow.displayName = "ScrollShadow";
